<template>
  <el-dialog
    visible
    width="25%"
    @close="abortCreate"
  >
    <ContentHeader
      slot="title"
      :title="isModalEdit ?
        $t('orders.update.updateOrder') : $t('orders.create.createOrder')"
    />
    <ValidationObserver v-slot="{ handleSubmit, invalid }">
      <el-form
        ref="form"
        label-position="top"
      >
        <el-row
          :gutter="20"
          type="flex"
        >
          <el-col :span="24">
            <FormItem
              rules="required"
              :label="$t('orders.create.title')"
              :help="$t('tooltips.orderName')"
            >
              <el-input
                v-model="formData.title"
              />
            </FormItem>
            <FormItem
              :label="$t('orders.create.customerOrderNumber')"
              :help="$t('tooltips.customerOrderNumber')"
            >
              <el-input
                v-model="formData.customerOrderNumber"
                :maxlength="20"
                show-word-limit
              />
            </FormItem>
            <FormItem
              v-if="userPermissions.includes('admin')"
              rules="required"
              :label="$t('orders.create.customerId')"
            >
              <el-autocomplete
                v-model="customerName"
                style="width: 100%"
                :fetch-suggestions="customersSearch"
                :trigger-on-focus="false"
                value-key="label"
                placeholder=""
                @select="handleSelectAutocomplete"
                @input="handleCustomerInput"
              />
            </FormItem>
            <FormItem
              v-if="userPermissions.includes('admin')"
              rules="required"
              :label="$t('orders.create.customerUser')"
            >
              <el-select
                v-model="formData.userId"
                style="width: 100%"
                :placeholder="$t('orders.create.customerUserSelect')"
                :disabled="!formData.customerId || loadingUsers"
                :loading="loadingUsers"
              >
                <el-option
                  v-for="user in customerUsers"
                  :key="user.id"
                  :label="`${user.givenName} ${user.familyName} (${user.email})`"
                  :value="user.id"
                />
              </el-select>
            </FormItem>
            <FormItem
              :label="$t('orders.create.separateInvoice')"
            >
              <el-switch
                v-model="formData.separateInvoice"
                :active-text="$t('orders.create.separateInvoiceYes')"
                :inactive-text="$t('orders.create.separateInvoiceNo')"
              />
            </FormItem>
          </el-col>
        </el-row>
        <el-row
          :gutter="20"
          type="flex"
        >
          <el-col :span="24">
            <p>{{ $t('global.requiredFields') }}</p>
          </el-col>
        </el-row>
        <FormButtons>
          <el-button
            type="secondary"
            @click.prevent="abortCreate"
          >
            {{ $t('orders.create.abortCreate') }}
          </el-button>
          <el-button
            v-if="isModalEdit"
            type="primary"
            native-type="submit"
            :disabled="invalid"
            @click.prevent="handleSubmit(updateOrder)"
          >
            {{ $t('orders.update.updateOrder') }}
          </el-button>
          <el-button
            v-else
            type="primary"
            native-type="submit"
            :disabled="invalid"
            @click.prevent="handleSubmit(createOrder)"
          >
            {{ $t('orders.create.createOrder') }}
          </el-button>
        </FormButtons>
      </el-form>
    </ValidationObserver>
  </el-dialog>
</template>

<script>
import { mapGetters } from 'vuex';
import { ordersApi, customersApi } from '@/api';
import FormItem from '@/components/FormItem.vue';
import ContentHeader from '@/components/ContentHeader.vue';
import FormButtons from '@/components/FormButtons.vue';

const defaultData = {
  title: '',
  customerId: '',
  userId: '',
  customerOrderNumber: '',
  separateInvoice: false,
};


export default {
  name: 'CreateOrder',
  components: {
    FormItem,
    ContentHeader,
    FormButtons,
  },
  props: {
    visible: { type: Boolean, default: false },
    isModalEdit: { type: Boolean, default: false },
    initialData: { type: Object, default: () => defaultData },
  },
  data() {
    return {
      formData: { ...defaultData },
      customerName: '',
      customerUsers: [],
      loadingUsers: false,
    };
  },
  computed: {
    ...mapGetters([
      'userPermissions',
      'loggedUser',
    ]),
    adminId() {
      return this.loggedUser?.id || null;
    },
  },
  watch: {
    initialData(data) {
      if (Object.keys(data).length > 0) {
        this.formData = { ...data };
      }
    },
  },
  methods: {
    async createOrder() {
      try {
        const basePayload = {
          title: this.formData.title,
          customerOrderNumber: this.formData.customerOrderNumber,
          separateInvoice: this.formData.separateInvoice,
        };
        const orderCreatedResponse = this.userPermissions.includes('admin')
          ? await ordersApi.create({
            ...basePayload,
            customerId: this.formData.customerId,
            userId: this.formData.userId,
            adminId: this.adminId,
          })
          : await ordersApi.create(basePayload);
        this.$notify({ showClose: true, type: 'success', message: this.$t('orders.create.success') });
        this.formData = { ...defaultData };
        this.customerUsers = [];
        this.$emit('dialogClose', { success: true, order: orderCreatedResponse.data });
      } catch (error) {
        this.$notify({ showClose: true, type: 'error', message: error.response.data.error.message });
      }
    },
    async updateOrder() {
      try {
        await ordersApi.update(this.$route.params.orderId, {
          name: this.formData.name,
          address: this.formData.address,
          vat: this.formData.vat,
          sdi: this.formData.sdi,
          certifiedEmail: this.formData.certifiedEmail,
          fiscalCode: this.formData.fiscalCode,
          isEnabled: this.formData.isEnabled,
        });
        this.$notify({ showClose: true, type: 'success', message: this.$t('orders.update.success') });
        this.formData = { ...defaultData };
        this.customerUsers = [];
        this.$emit('dialogClose', { success: true });
      } catch (error) {
        this.$notify({ showClose: true, type: 'error', message: error.response.data.error.message });
      }
    },
    abortCreate() {
      this.formData = { ...defaultData };
      this.customerUsers = [];
      this.$emit('dialogClose', { success: false });
    },
    async customersSearch(value, cb) {
      if (value.length >= 3) {
        const results = await customersApi.autocompleteFetch(value);
        const filteredResults = results.data.map(item => (
          {
            label: item.name,
            value: item.id,
          }
        ));
        cb(filteredResults);
      } else {
        cb([]);
      }
    },
    async handleSelectAutocomplete(item) {
      this.formData.customerId = item.value;
      this.formData.userId = ''; // Reset iniziale
      await this.getCustomerUsers(item.value);
    },
    handleCustomerInput() {
      this.formData.customerId = '';
      this.formData.userId = '';
      this.customerUsers = [];
    },
    async getCustomerUsers(customerId) {
      if (!customerId) return;
      this.loadingUsers = true;
      try {
        const response = await customersApi.getCustomerUsers(customerId);
        this.customerUsers = response.data;
      } catch (error) {
        this.$notify({
          showClose: true,
          type: 'error',
          message: this.$t('orders.create.errorLoadingUsers'),
        });
        this.customerUsers = [];
      } finally {
        this.loadingUsers = false;
      }
    },
  },
};
</script>
